import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, PaperPlaneRight, Paperclip, Camera, Image as ImageIcon, MapPin, Check, Truck, Package as PackageIcon, Printer, ArrowUp, FileArrowDown, Lock, Star } from '@phosphor-icons/react';
import API, { fileUrl } from '../lib/api';
import { useAuth } from '../lib/auth';

const STATUS_STEPS = [
  { key: 'pending_payment', label: 'Payment', color: '#8b5cf6' },
  { key: 'pending_partner', label: 'File is being reviewed', color: '#4f46e5' },
  { key: 'accepted', label: 'Accepted', color: '#00a3e0' },
  { key: 'printing', label: 'Printing', color: '#ffd700', dark: true },
  { key: 'printed', label: 'Printed', color: '#f97316' },
  { key: 'out_for_delivery', label: 'Out for delivery', color: '#ef4444' },
  { key: 'delivered', label: 'Delivered', color: '#10b981' },
];

const QUICK_REPLIES = [
  'Any update on my order?',
  "What's the timeline?",
  'Please share an ETA.',
  'Need this urgently — possible?',
];

const RatingCard = ({ order, onDone }) => {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (stars < 1) { toast.error('Please pick a star rating'); return; }
    setBusy(true);
    try {
      await API.post(`/orders/${order.order_id}/rate`, { stars, text: text.slice(0, 200) });
      toast.success('Thanks for the feedback!');
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit rating');
    } finally { setBusy(false); }
  };
  return (
    <div className="pk-card p-4 space-y-3" data-testid="rate-card">
      <div>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— How was the print?</div>
        <div className="font-display text-[18px] mt-1 leading-tight">Rate your print partner</div>
        <div className="text-[11px] text-neutral-500">Your rating helps them improve service quality.</div>
      </div>
      <div className="flex items-center gap-1" data-testid="rate-stars">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" onClick={() => setStars(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} className="p-1 transition-transform active:scale-90" data-testid={`rate-star-${n}`}>
            <Star size={28} weight={(hover || stars) >= n ? 'fill' : 'regular'} color={(hover || stars) >= n ? '#e30066' : '#a3a3a3'} />
          </button>
        ))}
      </div>
      <div>
        <textarea value={text} onChange={e => setText(e.target.value.slice(0, 200))} rows={3} className="pk-textarea" placeholder="Write a short review (optional, max 200 chars)" data-testid="rate-text" />
        <div className="text-[10px] text-neutral-500 text-right mt-1">{text.length}/200</div>
      </div>
      <button onClick={submit} disabled={busy || stars < 1} className="pk-btn pk-btn-magenta w-full" data-testid="rate-submit">
        {busy ? 'Submitting…' : 'Submit rating'}
      </button>
    </div>
  );
};

const OrderDetail = () => {
  const { oid } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [attachOpen, setAttachOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const [chatClosed, setChatClosed] = useState(false);
  const [chatClosedReason, setChatClosedReason] = useState(null);
  const cameraInputRef = useRef(null);
  const msgEndRef = useRef(null);

  const load = async () => {
    try {
      const r = await API.get(`/orders/${oid}`);
      setOrder(r.data);
      const m = await API.get(`/orders/${oid}/messages`);
      // Response shape: { messages: [...], chat_closed: bool, closed_reason: str|null }
      const list = Array.isArray(m.data) ? m.data : (m.data.messages || []);
      setChatClosed(Boolean(m.data?.chat_closed));
      setChatClosedReason(m.data?.closed_reason || null);
      // Detect new incoming messages (not from me) and play sound
      const prevLen = messages.length;
      if (prevLen > 0 && list.length > prevLen) {
        const newOnes = list.slice(prevLen).filter(msg => msg.sender_id !== user?.user_id);
        if (newOnes.length > 0) {
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ctx = new AC();
              const o = ctx.createOscillator(); const g = ctx.createGain();
              o.type = 'sine'; o.frequency.value = 880;
              g.gain.setValueAtTime(0, ctx.currentTime);
              g.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.01);
              g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
              o.connect(g).connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.3);
              setTimeout(() => ctx.close(), 500);
            }
          } catch { /* audio context blocked (autoplay policy or unsupported browser) — silent by design */ }
          toast.info('New message');
        }
      }
      setMessages(list);
    } catch (e) { toast.error('Failed to load order'); }
  };

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [oid]);
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const currentIdx = order ? STATUS_STEPS.findIndex(s => s.key === order.status) : -1;

  const sendMessage = async (payload) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await API.post(`/orders/${oid}/messages`, payload);
      setMessages(m => [...m, r.data]);
      setText('');
    } catch (e) {
      toast.error('Send failed');
    } finally { setBusy(false); }
  };

  const sendText = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    sendMessage({ text: t });
  };

  const handleAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await API.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await sendMessage({ file_id: r.data.file_id, text: text || null });
      setAttachOpen(false);
    } catch (err) { toast.error('Upload failed'); }
  };

  const doAction = async (path, body) => {
    try { await API.post(`/orders/${oid}/${path}`, body || {}); toast.success('Updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ---- Retry Payment (customer) ----
  const [retryBusy, setRetryBusy] = useState(false);
  const loadRzpScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  const retryPayment = async () => {
    setRetryBusy(true);
    try {
      const rzpRes = await API.post('/payment/create-order', { order_id: oid });
      const { rzp_order_id, amount, key_id, mock } = rzpRes.data;
      if (mock) {
        toast('Dev mode — simulating payment…');
        await new Promise(r => setTimeout(r, 700));
        await API.post('/payment/verify', {
          order_id: oid, razorpay_order_id: rzp_order_id,
          razorpay_payment_id: `pay_MOCK${Date.now()}`,
          razorpay_signature: `mock_sig_${Date.now()}`,
        });
        toast.success('Payment complete — finding a printer');
        load();
        return;
      }
      await loadRzpScript();
      const rzp = new window.Razorpay({
        key: key_id, amount, currency: 'INR', name: 'PrintingKit',
        order_id: rzp_order_id, prefill: { name: user?.name, email: user?.email, contact: order?.delivery_phone },
        theme: { color: '#e30066' },
        handler: async (resp) => {
          try {
            await API.post('/payment/verify', {
              order_id: oid,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success('Payment successful — finding a printer');
            load();
          } catch (err) { toast.error(err.response?.data?.detail || 'Verification failed'); }
        },
        modal: { ondismiss: () => toast('Payment cancelled') },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open payment');
    } finally { setRetryBusy(false); }
  };

  if (!order) return <div className="p-8 text-center text-sm">Loading…</div>;

  return (
    <div className="pb-4">
      <div className="px-5 pt-5 flex items-center justify-between">
        <Link to={user?.role === 'partner' ? '/partner' : '/orders'} className="pk-chip" data-testid="order-back"><ArrowLeft size={12} /> Back</Link>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">#{order.order_id.slice(-10)}</div>
      </div>

      <div className="px-5 mt-3 reg-mark">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Order</div>
        <h1 className="font-display text-[24px] mt-1 leading-tight">{order.items.map(i => `${i.quantity}× ${i.product_name}`).join(', ')}</h1>
        <div className="mt-2 font-mono text-[13px]">₹{(order.total_amount || 0).toLocaleString('en-IN')} · <span className="uppercase text-neutral-500 text-[10px] tracking-widest">{order.payment_status}</span></div>
      </div>

      {/* Vertical stepper */}
      <div className="px-5 mt-5">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-3">Status</div>
        <div className="relative">
          {STATUS_STEPS.map((s, i) => {
            const done = i < currentIdx;
            const current = i === currentIdx;
            const upcoming = i > currentIdx;
            const bg = done || current ? s.color : '#fff';
            const fg = (done || current) ? (s.dark ? '#0a0a0a' : '#fff') : '#a3a3a3';
            return (
              <div key={s.key} className="flex items-center gap-3 pb-4 relative">
                {i < STATUS_STEPS.length - 1 && (
                  <div className="absolute left-[15px] top-[32px] bottom-0 w-[2px]" style={{ background: done ? s.color : '#e5e5e5' }} />
                )}
                <div className="w-8 h-8 flex items-center justify-center border-2 border-black font-mono text-[11px] shrink-0 font-bold"
                  style={{ background: bg, color: fg }}>{String(i+1).padStart(2,'0')}</div>
                <div className="flex-1 flex items-center justify-between gap-2">
                  <div className={`font-bold text-[13px] ${upcoming ? 'text-neutral-400' : ''}`}>{s.label}</div>
                  {current && <span className="ml-auto shrink-0 text-[9px] tracking-[0.24em] font-bold uppercase px-1.5 py-0.5 border-2 border-black" style={{ background: s.color, color: s.dark ? '#0a0a0a' : '#fff' }}>Current</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Job brief — full customer selections (material, size, GSM, lamination, dimensions, colour profile, price breakdown) */}
      <div className="px-5 mt-5">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: 'var(--pk-primary)' }}>— Job brief</div>
        <div className="space-y-3">
          {order.items.map((it, idx) => {
            const rows = [];
            if (it.material) rows.push(['Material / GSM', it.material]);
            if (it.size) rows.push(['Size', it.size]);
            if (it.width && it.height) rows.push(['Dimensions', `${it.width} × ${it.height} ft`]);
            if (it.sqft) rows.push(['Area', `${Number(it.sqft).toFixed(2)} sq.ft`]);
            const lam = it.lamination_option || (it.lamination === true ? 'With lamination' : it.lamination === false ? 'No lamination' : null);
            if (lam) rows.push(['Lamination', lam]);
            if (it.color_profile) rows.push(['Colour profile', it.color_profile.toUpperCase()]);
            if (it.required_machine_types?.length) rows.push(['Required machines', it.required_machine_types.join(', ')]);
            const slug = (k) => k.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            return (
              <div key={idx} className="pk-card p-4" data-testid={`job-brief-item-${idx}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bold text-[15px]">{it.product_name}</div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 mt-0.5">Item {idx + 1} of {order.items.length}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Qty</div>
                    <div className="font-stat text-[24px] leading-none" data-testid={`job-brief-qty-${idx}`}>{it.quantity}</div>
                  </div>
                </div>
                {rows.length > 0 && (
                  <div className="mt-3 divide-y divide-neutral-200 border-t border-neutral-200">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex items-start justify-between py-1.5 text-[12px]">
                        <div className="text-[10px] tracking-[0.16em] uppercase text-neutral-500 pt-0.5">{k}</div>
                        <div className="font-bold text-right ml-3 max-w-[60%] break-words" data-testid={`job-brief-${slug(k)}-${idx}`}>{v}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t-2 border-black flex items-center justify-between text-[12px] font-mono">
                  <span className="text-neutral-500">
                    {it.unit_price ? `${it.quantity} × ₹${it.unit_price.toFixed(2)}` : `Line total`}
                  </span>
                  <span className="font-bold">₹{(it.total || 0).toLocaleString('en-IN')}</span>
                </div>
                {(it.file_id || it.file_link) && (
                  <div className="mt-2 text-[11px]">
                    <span className="text-[9px] tracking-[0.22em] uppercase text-neutral-500 mr-1">Artwork:</span>
                    {it.file_link ? (
                      <a href={it.file_link} target="_blank" rel="noreferrer" className="underline break-all" data-testid={`job-brief-link-${idx}`}>{it.file_link}</a>
                    ) : (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.file_id}/download`} target="_blank" rel="noreferrer" className="underline" data-testid={`job-brief-file-${idx}`}>Download uploaded file</a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {order.notes && (
          <div className="pk-card p-3 mt-3" style={{ background: 'var(--pk-yellow)' }} data-testid="job-brief-notes">
            <div className="text-[9px] tracking-[0.22em] uppercase font-bold mb-1">Customer note</div>
            <div className="text-[12px]">{order.notes}</div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 mt-2 space-y-2">
        {user?.role === 'customer' && (order.status === 'pending_payment' || order.payment_status === 'failed' || order.payment_status === 'unpaid') && (
          <button
            className="pk-btn pk-btn-magenta w-full"
            onClick={retryPayment}
            disabled={retryBusy}
            data-testid="retry-payment-btn"
          >
            <Lock size={16} weight="fill" /> {retryBusy ? 'Opening payment…' : `Retry Payment · ₹${(order.total_amount || 0).toLocaleString('en-IN')}`}
          </button>
        )}
        {user?.role === 'customer' && (order.status === 'out_for_delivery' || order.status === 'printed') && (
          <div className="space-y-1">
            <button
              className="pk-btn w-full received-blink"
              onClick={() => doAction('received')}
              data-testid="received-btn"
              style={{
                background: '#10b981', color: '#fff',
                borderColor: '#065f46', textShadow: '0 1px 0 rgba(0,0,0,0.15)',
              }}
            >
              <Check size={18} weight="bold" /> I Received It
            </button>
            <div className="text-[10px] text-neutral-500 tracking-wider text-center" data-testid="received-hint">
              Tapping this button means you have received this item in right condition.
            </div>
          </div>
        )}
        {user?.role === 'customer' && order.status === 'delivered' && !order.rating_stars && (
          <RatingCard order={order} onDone={load} />
        )}
        {user?.role === 'customer' && order.status === 'delivered' && order.rating_stars && (
          <div className="pk-card p-3 text-center" style={{ background: 'var(--pk-yellow)' }}>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold">Your rating</div>
            <div className="mt-1 flex justify-center gap-0.5" data-testid="your-rating">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={16} weight={i < order.rating_stars ? 'fill' : 'regular'} color={i < order.rating_stars ? '#e30066' : '#0a0a0a'} />
              ))}
            </div>
            {order.rating_text && <div className="mt-2 text-[12px] italic">"{order.rating_text}"</div>}
          </div>
        )}
        {user?.role === 'partner' && order.status === 'pending_partner' && (
          <div className="grid grid-cols-2 gap-2">
            <button className="pk-btn pk-btn-magenta" onClick={() => doAction('accept')} data-testid="partner-accept">Accept</button>
            <button className="pk-btn pk-btn-outline" onClick={() => doAction('reject')} data-testid="partner-reject">Reject</button>
          </div>
        )}
        {user?.role === 'partner' && order.status === 'accepted' && (
          <button className="pk-btn w-full" onClick={() => doAction('status', { status: 'printing' })} data-testid="partner-printing"><Printer size={14}/> Start printing</button>
        )}
        {user?.role === 'partner' && order.status === 'printing' && (
          <button className="pk-btn w-full" onClick={() => doAction('status', { status: 'printed' })} data-testid="partner-printed">Mark printed</button>
        )}
        {user?.role === 'partner' && order.status === 'printed' && (
          <button className="pk-btn w-full" onClick={() => doAction('status', { status: 'out_for_delivery' })} data-testid="partner-ofd"><Truck size={14}/> Out for delivery</button>
        )}
        {order.delivery_map_link && user?.role === 'partner' && (
          <a href={order.delivery_map_link} target="_blank" rel="noreferrer" className="pk-btn pk-btn-outline w-full" data-testid="open-map"><MapPin size={14}/> Open pin in Maps</a>
        )}
        {order.items?.some(i => i.file_id || i.file_link) && (
          <div className="pk-card p-3">
            <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 mb-2">Uploaded artwork</div>
            <div className="space-y-1.5">
              {order.items.map((it, idx) => {
                if (!it.file_id && !it.file_link) return null;
                const href = it.file_id ? fileUrl(it.file_id) : it.file_link;
                return (
                  <a key={idx} href={href} target="_blank" rel="noreferrer" download className="flex items-center gap-2 px-2 py-2 border-2 border-black hover:bg-yellow-50" data-testid={`view-file-${idx}`}>
                    <FileArrowDown size={16} weight="fill" />
                    <div className="flex-1 text-[12px] font-mono truncate">{it.product_name} · {it.file_id ? 'uploaded file' : 'external link'}</div>
                    <span className="text-[10px] tracking-widest uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>{it.file_id ? 'Download' : 'Open'}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="px-5 mt-5">
        <div className="pk-card p-4">
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">Delivery</div>
          <div className="text-[13px] mt-1">{order.delivery_address}</div>
          <div className="text-[12px] text-neutral-600 mt-1 font-mono">PIN {order.delivery_pincode}</div>
          <div className="text-[12px] font-mono mt-1">
            {order.delivery_phone_masked ? (
              <span className="italic text-neutral-400">Phone hidden until accepted</span>
            ) : (order.delivery_phone || '—')}
          </div>
        </div>
      </div>

      {/* Chat */}
      <div className="px-5 mt-6">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2">Chat</div>
        <div
          className="pk-border p-3 space-y-1"
          style={{
            minHeight: 200,
            maxHeight: 340,
            overflowY: 'auto',
            background: 'linear-gradient(180deg, #fafafa 0%, #f4f4f5 100%)',
          }}
          data-testid="chat-messages"
        >
          {messages.length === 0 && (
            <div className="text-center text-[12px] text-neutral-400 py-10">Say hi 👋</div>
          )}
          {messages.map((m, idx) => {
            const mine = m.sender_id === user?.user_id;
            const prev = messages[idx - 1];
            const grouped = prev && prev.sender_id === m.sender_id;
            const ts = m.created_at ? new Date(m.created_at) : null;
            const timeStr = ts && !isNaN(ts) ? ts.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
            const seen = (m.seen_by || []).length > 1;
            return (
              <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'} ${grouped ? 'mt-0.5' : 'mt-2'}`}>
                <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'} max-w-[78%]`}>
                  <div
                    className={`relative px-3 py-2 shadow-sm ${
                      mine
                        ? 'rounded-2xl rounded-br-md text-white'
                        : 'rounded-2xl rounded-bl-md bg-white text-neutral-900 border border-neutral-200'
                    }`}
                    style={mine ? { background: 'var(--pk-primary)' } : undefined}
                    data-testid={mine ? 'chat-bubble-mine' : 'chat-bubble-theirs'}
                  >
                    {m.file_id && (
                      <a href={fileUrl(m.file_id)} target="_blank" rel="noreferrer" className="block mb-1">
                        <img
                          src={fileUrl(m.file_id)}
                          alt=""
                          className="max-w-full max-h-56 rounded-lg"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </a>
                    )}
                    {m.text && (
                      <div className="text-[14px] leading-snug whitespace-pre-wrap break-words">{m.text}</div>
                    )}
                    <div className={`flex items-center gap-1 mt-1 text-[10px] ${mine ? 'justify-end text-white/80' : 'justify-start text-neutral-400'}`}>
                      {timeStr && <span>{timeStr}</span>}
                      {mine && (
                        <span data-testid="chat-seen-indicator" style={{ color: seen ? '#7dd3fc' : 'rgba(255,255,255,0.7)' }}>
                          {seen ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={msgEndRef} />
        </div>

        {user?.role === 'customer' && !chatClosed && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid="quick-replies">
            {QUICK_REPLIES.map(q => (
              <button key={q} onClick={() => sendMessage({ text: q })} className="pk-chip text-[10px]" data-testid="quick-reply">{q}</button>
            ))}
          </div>
        )}

        {chatClosed ? (
          <div className="mt-3 pk-card p-4 flex items-center gap-3" style={{ background: 'color-mix(in srgb, var(--pk-primary) 8%, #ffffff)', borderColor: 'var(--pk-primary)' }} data-testid="chat-closed-banner">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--pk-primary)' }}>
              <Check size={16} weight="bold" color="#fff" />
            </div>
            <div className="text-[12px]">
              <div className="font-bold text-[13px] leading-tight">Chat closed</div>
              <div className="text-neutral-600 mt-0.5 leading-snug">{chatClosedReason || 'This order is complete. Messages are read-only.'}</div>
            </div>
          </div>
        ) : (
          <form onSubmit={sendText} className="mt-2 flex items-center gap-2">
            <div className="flex-1 flex items-center bg-white pk-border rounded-full px-3 py-1">
              <div className="relative">
                <button type="button" onClick={() => setAttachOpen(!attachOpen)} className="p-2" data-testid="chat-attach-btn">
                  <Paperclip size={18} weight="bold" />
                </button>
                {attachOpen && (
                  <div className="absolute bottom-12 left-0 z-30 pk-card p-1 min-w-[180px]">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-yellow-100" data-testid="attach-gallery">
                      <ImageIcon size={16} /> <span className="text-[13px]">Photo from Gallery</span>
                    </button>
                    <button type="button" onClick={() => cameraInputRef.current?.click()} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-yellow-100" data-testid="attach-camera">
                      <Camera size={16} /> <span className="text-[13px]">Take Photo</span>
                    </button>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAttach} className="hidden" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleAttach} className="hidden" />
              </div>
              <input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message…" className="flex-1 outline-none text-[13px] px-1" data-testid="chat-input" />
            </div>
            <button type="submit" disabled={!text.trim()} className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-black brutal-shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'var(--pk-primary)', color: '#fff' }} data-testid="chat-send">
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;
